import { SIDE_BETS, type SideBetId } from '@tv-casino/shared';
import styles from './SideBetPicker.module.css';

/** Chip step for side bets: small, because these are long-shot wagers. */
const STEP = 5;

/**
 * Stakes the optional side bets alongside the main wager. Nothing is sent here
 * — the totals ride along with the next `player_bet`, so the whole round's
 * stake is committed in one message.
 */
export function SideBetPicker({
  enabled,
  max,
  budget,
  value,
  busy,
  onChange,
}: {
  /** Side bets the host has switched on for this table. */
  enabled: readonly SideBetId[];
  /** Per-bet ceiling from the table rules. */
  max: number;
  /** Chips still free after the main bet is staged. */
  budget: number;
  value: Partial<Record<SideBetId, number>>;
  busy: boolean;
  onChange: (next: Partial<Record<SideBetId, number>>) => void;
}) {
  if (enabled.length === 0) return null;

  const staked = Object.values(value).reduce((sum, n) => sum + (n ?? 0), 0);

  const set = (id: SideBetId, amount: number) => {
    const next = { ...value };
    if (amount <= 0) delete next[id];
    else next[id] = amount;
    onChange(next);
  };

  return (
    <section className={styles.wrap}>
      <p className={styles.heading}>Side bets</p>
      {enabled.map((id) => {
        const def = SIDE_BETS[id];
        if (!def) return null;
        const amount = value[id] ?? 0;
        const room = budget - staked;
        return (
          <div key={id} className={styles.row}>
            <div className={styles.info}>
              <p className={styles.name}>{def.name}</p>
              <p className={styles.odds}>
                {def.paytable.map((row) => `${row.label} ${row.odds}`).join(' · ')}
              </p>
            </div>
            <div className={styles.stepper}>
              <button
                type="button"
                className={styles.step}
                disabled={busy || amount === 0}
                onClick={() => set(id, amount - STEP)}
                aria-label={`Lower the ${def.name} bet`}
              >
                −
              </button>
              <span className={amount > 0 ? styles.amountOn : styles.amount}>{amount}</span>
              <button
                type="button"
                className={styles.step}
                disabled={busy || amount + STEP > max || room < STEP}
                onClick={() => set(id, amount + STEP)}
                aria-label={`Raise the ${def.name} bet`}
              >
                +
              </button>
            </div>
          </div>
        );
      })}
    </section>
  );
}
